import React from "react";
import { Link } from "react-router-dom";
import PageMeta from "./PageMeta";
import PublicPageLayout from "./PublicPageLayout";

const rows = [
  {
    name: "Google Cloud / Firebase",
    purpose:
      "Authentication, database (Firestore), file storage (e.g. application PDFs and estimate images), serverless backend (Cloud Functions), secret management (Secret Manager), and web hosting. Operates as the primary infrastructure for the platform.",
    notes: "Includes Firebase Auth, Firestore, Cloud Storage, Cloud Functions, Cloud Secret Manager, and Firebase Hosting where used.",
  },
  {
    name: "Google AI (Gemini API)",
    purpose:
      "Optional processing of rental application PDFs to suggest structured fields (e.g. name, contact, employment) when a licensed user requests extraction. Content is sent to Google's generative AI service only for that operation.",
    notes: "Configured via server-side credentials; not embedded in the public website client for extraction.",
  },
  {
    name: "Stripe",
    purpose:
      "Payment processing when you use billing or payment features—including the professional onboarding deposit, tenant service fees, and maintenance-related payments. Stripe Connect is used to facilitate earnings payouts to approved service professionals.",
    notes: "Card and payment data are handled according to Stripe's terms and PCI requirements. Professionals who link a payout account are subject to the Stripe Connected Account Agreement in addition to these terms.",
  },
  {
    name: "Background screening partner",
    purpose:
      "Identity verification and, where permitted by applicable law for your role and region, background screening for service professionals during onboarding. Screening is conducted by a licensed Consumer Reporting Agency (CRA).",
    notes: "Screening is not performed without your acknowledgment at the time of deposit payment. The specific partner is disclosed at the time of screening. Applicable law (including the FCRA where it applies) governs your rights regarding the report.",
  },
  {
    name: "Google Workspace / Gmail API",
    purpose:
      "Sending certain transactional emails (for example, property owner invitations and onboarding notifications) from our systems when that workflow is enabled.",
    notes: "Message content and recipient addresses are processed to deliver the email.",
  },
  {
    name: "Google Maps Platform",
    purpose: "Maps and place autocomplete in the application where those features are enabled—for example, service area selection during professional onboarding.",
    notes: "May receive queries you type (e.g. addresses) as part of providing map and location services.",
  },
];

const SubprocessorsPage = () => {
  return (
    <PublicPageLayout
      eyebrow="Trust"
      title="Subprocessors"
      intro="Third-party services that process personal information on our behalf to operate Tenant Dispatch. This list is for transparency; your agreements with us and applicable law govern your rights."
    >
      <PageMeta
        title="Subprocessors | Tenant Dispatch"
        description="Third-party services (subprocessors) used by Tenant Dispatch, including cloud hosting, AI-assisted PDF processing, payments, Stripe Connect, background screening, and email."
        canonical="https://tenantdispatch.com/subprocessors"
      />

      <div className="public-page-prose">
        <p>
          <strong>Last updated:</strong> April 12, 2026
        </p>
        <p>
          We use the vendors below to host and run the platform. They may process personal information only as needed to provide their service to us. We do not sell your personal information. For how we collect and use data overall, see our{" "}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
        <p>
          Vendor practices (including subprocessors they use) are described in each vendor's own documentation. Where we enter into a data processing agreement, it supplements these disclosures.
        </p>

        <h2>Subprocessors</h2>
        <div className="subprocessors-table-wrap">
          <table className="subprocessors-table">
            <thead>
              <tr>
                <th scope="col">Service provider</th>
                <th scope="col">Role</th>
                <th scope="col">Additional detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name}>
                  <th scope="row">{r.name}</th>
                  <td>{r.purpose}</td>
                  <td>{r.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2>Updates</h2>
        <p>
          We may add or replace subprocessors as we improve the product. We will update this page when we make material changes and adjust the "Last updated" date. For questions, contact{" "}
          <a href="mailto:contact@tenantdispatch.com">contact@tenantdispatch.com</a>.
        </p>

        <p className="public-page-disclaimer">
          This page is provided for transparency and does not constitute legal advice. If you are a brokerage or enterprise customer, your order form or data processing addendum may list additional or different terms.
        </p>
      </div>
    </PublicPageLayout>
  );
};

export default SubprocessorsPage;
